import {
  Model,
  QuestionCustomModel,
  QuestionPanelDynamicModel,
  Serializer,
  SurveyError,
} from "survey-core";
import {
  ComponentCollection,
  ICustomQuestionTypeConfiguration,
} from "survey-core";
import { SessionView } from "shared/mappers/database/session/session";
const CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI =
  "certification_attribution_multi";

const CertificationAttributionMultipleQuestionProperties = {
  numberOfItems: "numberOfItems",
  maxCertShareCount: "maxCertShareCount",
  maxShareHours: "maxShareHours",
};

interface CertificationAttributionPanelResponse {
  enabled: boolean;
  hoursToUse?: number;
  certificationDefinitionId?: number;
}

/**
 * Validates a certification attribution panel
 * @param panelData - The submitted values from the panel
 * @param panel - The panel instance for accessing configuration
 * @param surveyData - The full survey response data for cross-field validation
 * @param panelConfig - The configuration for this specific panel
 * @param questionProperties - The custom question properties (numberOfItems, certificationConfigs, etc.)
 * @returns String error message if invalid, undefined if valid
 */
export const validateCertificationAttributionPanel = (
  surveyData?: Record<string, unknown>,
  questionProperties?: {
    numberOfItems?: number;
    maxCertShareCount?: number;
    maxShareHours?: number;
  },
): string | undefined => {
  // Ported validation logic from prototype
  const maxShareHours = questionProperties?.maxShareHours ?? 12;
  const maxShareCert = questionProperties?.maxCertShareCount ?? 2;

  // Get LU hours (hours_earned)
  const luHours = Number(surveyData?.hours_earned ?? 0);
  if (!luHours || isNaN(luHours)) {
    return "Hours earned is required for certification attribution";
  }

  // Get allocations from enabled panels
  const panelDataArray = (surveyData?.[
    CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI
  ] ?? []) as CertificationAttributionPanelResponse[];
  const allocations = panelDataArray.map((panel) =>
    panel.enabled ? Number(panel.hoursToUse ?? 0) : 0,
  );

  // If no panels are enabled (no allocations), return valid (undefined)
  if (panelDataArray.every((panel) => !panel.enabled)) {
    return undefined;
  }

  const errors: string[] = [];

  // Check if any single allocation exceeds total hours earned
  if (allocations.some((h) => h > luHours)) {
    errors.push("Individual allocation cannot exceed total hours earned.");
  }

  // Process allocations sequentially to validate sharing logic
  let remainingBaseHours = luHours;
  let certsUsingShare = 0;

  for (const allocation of allocations) {
    if (allocation <= 0) {
      continue; // Skip disabled/zero allocations
    }
    if (remainingBaseHours > 0) {
      // Calculate how much this allocation exceeds remaining base hours
      const shareDifference = allocation - remainingBaseHours;
      remainingBaseHours = Math.max(0, remainingBaseHours - allocation);

      // If this allocation requires sharing (exceeds remaining base)
      if (shareDifference > 0) {
        if (shareDifference > maxShareHours) {
          errors.push("Shared hours exceed limit.");
          break;
        }
        certsUsingShare += 1;
      }
    } else {
      // No base hours remaining, this allocation is entirely from sharing
      if (allocation > maxShareHours) {
        errors.push("Shared hours exceed limit.");
        break;
      }
      certsUsingShare += 1;
    }
  }

  // Check if too many certs are receiving shared hours
  if (certsUsingShare > maxShareCert) {
    errors.push("Too many certs are using shared hours");
  }

  if (errors.length > 0) {
    return errors.join(" ");
  }
  return undefined;
};

/**
 * Validates the entire survey with access to all form data
 * @param survey - The survey instance with all data
 * @param questionName - The name of the certification attribution question
 * @param certificationConfigs - The certification configurations for each panel
 * @returns Array of validation errors
 */
export const validateCertificationAttributionSurvey = (
  survey: { data: Record<string, unknown> },
  questionName: string,
  questionProperties?: {
    numberOfItems: number;
    maxCertShareCount: number;
    maxShareHours: number;
  },
): { questionName: string; error: string } | undefined => {
  const panelError = validateCertificationAttributionPanel(
    survey.data,
    questionProperties,
  );
  if (panelError) {
    return {
      questionName: questionName,
      error: panelError,
    };
  }
  return undefined;
};

export const registerPropertiesCertificationAttributionMultipleQuestion =
  () => {
    Serializer.addProperty(CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI, {
      name: "numberOfItems",
      displayName: "Number of Certification Attribution Items",
      type: "number",
      isRequired: false,
      default: 4,
      category: "General",
    });
    Serializer.addProperty(CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI, {
      name: "maxCertShareCount",
      displayName: "Maximum Number of Certifications That Can Be Shared",
      type: "number",
      isRequired: false,
      default: 2,
      category: "General",
    });
    Serializer.addProperty(CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI, {
      name: "maxShareHours",
      displayName: "Maximum Number of hours that can be shared",
      type: "number",
      isRequired: false,
      default: 12,
      category: "General",
    });

    // Add individual properties for easier configuration
    for (let i = 1; i <= 10; i++) {
      Serializer.addProperty(CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI, {
        name: `item${i}DefinitionId`,
        displayName: `Item ${i} - Certification Definition ID`,
        type: "number",
        isRequired: false,
        category: "General",
      });
      Serializer.addProperty(CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI, {
        name: `item${i}DefinitionName`,
        displayName: `Item ${i} - Certification Definition Name`,
        type: "string",
        isRequired: false,
        category: "General",
      });
    }
  };

export type CertificationAttributionMultipleQuestionProps = {
  session: SessionView;
};

export const RegisterCertificationAttributionMultipleQuestionType = async ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  session,
}: CertificationAttributionMultipleQuestionProps) => {
  registerPropertiesCertificationAttributionMultipleQuestion();

  const CertificationAttributionConfig: ICustomQuestionTypeConfiguration = {
    name: CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI,
    defaultQuestionTitle: "Select Certification Attribution and Hours to Use",
    questionJSON: {
      type: "paneldynamic",
      name: "certification_attribution_panel",
      title: "Certification Attribution Selection",
      allowRemovePanel: false,
      allowAddPanel: false,
      titleLocation: "hidden",
      templateElements: [
        {
          titleLocation: "left",
          type: "boolean",
          name: "enabled", // Will be updated per panel in onLoaded
          renderAs: "checkbox",
          startWithNewLine: false,
          defaultValue: false,
          title: "Enable Certification Attribution", // Will be updated per panel in onLoaded
        },
        {
          titleLocation: "left",
          type: "text",
          name: "hoursToUse",
          inputType: "number",
          title: "Applied Hours",
          startWithNewLine: false,
          isRequired: false,
          placeHolder: "Enter hours to use",
        },
        {
          type: "text",
          name: "certificationDefinitionId",
          visible: false,
          defaultValue: null,
        },
      ],
    },
    onInit: () => {
      registerPropertiesCertificationAttributionMultipleQuestion();
    },
    onLoaded: (question: QuestionCustomModel) => {
      // Get certification configs first, as we'll need them for validation
      const numberOfItems =
        question.getPropertyValue(
          CertificationAttributionMultipleQuestionProperties.numberOfItems,
        ) || 1;

      const certificationConfigs: any[] = [];
      for (let i = 1; i <= numberOfItems && i <= 10; i++) {
        const definitionId = question.getPropertyValue(`item${i}DefinitionId`);
        const definitionName = question.getPropertyValue(
          `item${i}DefinitionName`,
        );

        if (definitionId || definitionName) {
          certificationConfigs.push({
            certificationDefinitionId: definitionId,
            certificationDefinitionName: definitionName,
          });
        } else {
          // Add empty config to maintain index
          certificationConfigs.push({});
        }

        // Register survey-level validation for this question
        const survey = question.survey as Model;
        if (survey) {
          // Check if we've already registered validation for this survey
          const validationKey = `__certificationValidation_${question.name}`;
          if (!(survey as unknown as Record<string, unknown>)[validationKey]) {
            // Mark that we've registered validation for this question
            (survey as unknown as Record<string, unknown>)[validationKey] =
              true;

            // Add survey-level validation
            survey.onValidateQuestion.add((sender, options) => {
              if (options.name === question.name) {
                const errors = validateCertificationAttributionSurvey(
                  sender,
                  options.name,
                  getCertificationAttributionMultipleQuestionProperties(
                    question,
                  ),
                );
                if (errors) {
                  options.error = errors.error;
                }
              }
            });

            // Also add validation on survey completion
            survey.onCompleting.add((sender, options) => {
              const errors = validateCertificationAttributionSurvey(
                sender,
                question.name,
                getCertificationAttributionMultipleQuestionProperties(question),
              );
              if (errors) {
                options.allowComplete = false;
                // You could also set errors on specific questions here
                const targetQuestion = sender.getQuestionByName(question.name);
                if (targetQuestion) {
                  targetQuestion.errors.push({
                    text: errors.error,
                  } as SurveyError);
                }
              }
            });
          }
        }

        const panelDynamic = <QuestionPanelDynamicModel>(
          question.contentQuestion
        );

        // Ensure the panelDynamic question is properly initialized
        if (!panelDynamic) {
          console.error("Panel dynamic question not found");
          return;
        }

        // Add validation to the paneldynamic question if onValidatePanel exists
        if (panelDynamic.onValidatePanel && panelDynamic.onValidatePanel.add) {
          panelDynamic.onValidatePanel.add((sender: any, options: any) => {
            // Get all question properties
            const questionProperties = {
              numberOfItems: question.getPropertyValue("numberOfItems"),
              certificationConfigs: question.getPropertyValue(
                "certificationConfigs",
              ),
              hoursRequired: question.getPropertyValue("hoursRequired"),
              completedAgoDays: question.getPropertyValue("completedAgoDays"),
              completedAgoMonths:
                question.getPropertyValue("completedAgoMonths"),
              completedAgoYears: question.getPropertyValue("completedAgoYears"),
            };

            const validationError = validateCertificationAttributionPanel(
              sender.survey?.data,
              questionProperties,
            );
            if (validationError) {
              options.error = validationError;
            }
          });
        }

        // Set panel count based on numberOfItems
        panelDynamic.panelCount = numberOfItems;

        // Configure each panel with its specific certification definition
        panelDynamic.panels.forEach((panel, index) => {
          const config = certificationConfigs[index];

          // Set the title for each panel based on the configuration or use default
          if (config && config.certificationDefinitionName) {
            panel.title = "";
          } else {
            panel.title = `Certification Attribution ${index + 1}`;
          }

          // Set the checkbox label (title) to the certificationDefinitionName if available
          const enabledQuestion = panel.getQuestionByName("enabled");
          if (enabledQuestion) {
            if (config && config.certificationDefinitionName) {
              enabledQuestion.title = config.certificationDefinitionName;
            } else {
              enabledQuestion.title = `Enable Certification Attribution ${index + 1}`;
            }
          }

          // Configure the hours input question in this panel
          const hoursQuestion = panel.getQuestionByName("hoursToUse");
          if (hoursQuestion) {
            // Make hours input dependent on enabled checkbox
            hoursQuestion.visibleIf = "{panel.enabled} = true";
            hoursQuestion.enableIf = "{panel.enabled} = true";
          }

          // Set the certificationDefinitionId value for this panel
          const certificationIdQuestion = panel.getQuestionByName(
            "certificationDefinitionId",
          );
          if (
            certificationIdQuestion &&
            config &&
            config.certificationDefinitionId
          ) {
            certificationIdQuestion.value = config.certificationDefinitionId;
          }
        });
      }
    },
  };

  if (
    !ComponentCollection.Instance.getCustomQuestionByName(
      CUSTOM_TYPE_CERTIFICATION_ATTRIBUTION_MULTI,
    )
  ) {
    ComponentCollection.Instance.add(CertificationAttributionConfig);
  }
};

const getCertificationAttributionMultipleQuestionProperties = (
  question: QuestionCustomModel,
) => {
  return {
    numberOfItems: question.getPropertyValue(
      CertificationAttributionMultipleQuestionProperties.numberOfItems,
    ),
    maxCertShareCount: question.getPropertyValue(
      CertificationAttributionMultipleQuestionProperties.maxCertShareCount,
    ),
    maxShareHours: question.getPropertyValue(
      CertificationAttributionMultipleQuestionProperties.maxShareHours,
    ),
  };
};
